import {
	OrderQuoteGuestAccessTokenStatus,
	useGetOrderQuoteGuestAccessTokensQuery,
} from '@codezo/graphql';
import { QuoteClient } from '@codezo/quote-detail';
import { ArrowUpRight, Plus } from 'lucide-react';
import { useState } from 'react';
import { ManageGuestLinksModal } from '../../../components/quotes/modals/ManageGuestLinksModal';
import { SendGuestLinkModal } from '../../../components/quotes/modals/SendGuestLinkModal';
import { Button } from '../../../components/ui/button';
import { InitialsAvatar } from '../../../components/ui/initials-avatar';

/**
 * Who the quote is for.
 *
 * Guest access is one line here, as the design has it: how many links are out,
 * and a way to send another. Listing them with their statuses — and revoking one
 * — is a different job, so it opens in `ManageGuestLinksModal` rather than
 * growing this card into a panel. See the note on `QuoteClient`.
 *
 * A link can be issued at any point in a quote's life. It used to require an
 * offer already sent; the backend dropped that gate, since a guest's view of
 * offers filters on `sentToClientAt` independently — a link minted early simply
 * shows nothing until batches go out.
 */
export function QuoteClientV2({
	quoteId,
	organization,
	onOpenClient,
	onGuestLinksChanged,
}: {
	quoteId: string;
	organization: { id: string; name: string; logo?: string | null };
	onOpenClient: () => void;
	/** Guest-link holders are always emailed, so who gets emailed changes too. */
	onGuestLinksChanged?: () => void;
}) {
	const [sendOpen, setSendOpen] = useState(false);
	const [manageOpen, setManageOpen] = useState(false);

	const { data, refetch } = useGetOrderQuoteGuestAccessTokensQuery({
		variables: { orderQuoteId: quoteId },
		fetchPolicy: 'no-cache',
	});

	const tokens = data?.getOrderQuoteGuestAccessTokens ?? [];
	const active = tokens.filter((t) => t.status === OrderQuoteGuestAccessTokenStatus.Active);

	/* Who has access, not how many — a count answers the wrong question. The
	   addresses are the thing an admin scans this line for, so they are the line.
	   Three fit before the row wraps past the card's two-row shape; the rest are
	   a count that opens the full list. What a link grants is explained on the
	   empty state and in the dialog, where it is new information. */
	const SHOWN = 3;
	const summary =
		active.length === 0 ? (
			'No links sent · a link opens the whole quote, for one email address'
		) : (
			<span className="flex flex-wrap items-center gap-1.5">
				{active.slice(0, SHOWN).map((token) => (
					<span
						key={token.id}
						title={token.email}
						className="max-w-[220px] truncate rounded-[var(--r-1)] bg-surface-fill px-1.5 py-0.5 font-mono text-xs text-text-2"
					>
						{token.email}
					</span>
				))}
				{active.length > SHOWN && (
					<button
						type="button"
						onClick={() => setManageOpen(true)}
						className="cursor-pointer bg-transparent p-0 text-xs text-text-3 underline-offset-2 transition-colors hover:text-text-1 hover:underline"
					>
						+{active.length - SHOWN} more
					</button>
				)}
			</span>
		);

	return (
		<>
			<QuoteClient
				name={organization.name}
				meta="Organization"
				avatar={
					<InitialsAvatar name={organization.name} src={organization.logo} size={40} />
				}
				action={
					<Button variant="outline" size="sm" onClick={onOpenClient}>
						<ArrowUpRight size={13} data-icon="inline-start" />
						Open client
					</Button>
				}
				guestAccess={{
					summary,
					action: (
						<span className="flex items-center gap-1">
							{/* Only once something exists to manage — revoking is the
							    reason this is here, and there is nothing to revoke on a
							    quote that has never issued a link. */}
							{tokens.length > 0 && (
								<Button
									variant="ghost"
									size="sm"
									onClick={() => setManageOpen(true)}
								>
									Manage
								</Button>
							)}
							<Button variant="ghost" size="sm" onClick={() => setSendOpen(true)}>
								<Plus size={13} data-icon="inline-start" />
								Send a link
							</Button>
						</span>
					),
				}}
			/>

			<SendGuestLinkModal
				open={sendOpen}
				onOpenChange={setSendOpen}
				orderQuoteId={quoteId}
				// Resending to an address supersedes its existing link, so the modal
				// warns before it happens.
				activeEmails={active.map((t) => t.email)}
				onSuccess={() => {
					void refetch();
					onGuestLinksChanged?.();
				}}
			/>

			<ManageGuestLinksModal
				open={manageOpen}
				onOpenChange={setManageOpen}
				orderQuoteId={quoteId}
				onChanged={() => {
					void refetch();
					onGuestLinksChanged?.();
				}}
			/>
		</>
	);
}
